import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MapPin, Clock, Phone, ChevronRight, Star, Navigation, Car, Coffee, IceCream, Heart, Users, Sparkles, Calendar, Gift, Camera } from 'lucide-react';
import { Button } from '../components/ui/button';
import { trackEvent } from '../lib/ga';

// Fallback content for cities without CMS data
const fallbackCityData = {
  'ice-cream-mineral-wells-tx': {
    city: 'Mineral Wells',
    distance: 'Right Here in',
    driveTime: '0 min',
    tagline: 'Your Local Ice Cream Paradise',
    heroTitle: 'Mineral Wells\' Favorite Ice Cream Shop',
    heroSubtitle: 'Serving our community with all-natural, handcrafted ice cream since day one. Located in the heart of historic downtown!',
    metaDescription: 'Best ice cream shop in Mineral Wells, TX. All-natural ice cream, famous Stackers, pastries & more at 103 NE 1st Ave. Family-owned, locally loved!',
    keywords: ['ice cream Mineral Wells', 'dessert Mineral Wells TX', 'ice cream shop downtown Mineral Wells', 'best ice cream Palo Pinto County'],
    localLandmarks: [
      { name: 'Historic Baker Hotel', distance: '2 min walk', description: 'Right across from the iconic Baker Hotel renovation!' },
      { name: 'Downtown Square', distance: '1 min walk', description: 'Steps from the historic downtown courthouse' },
      { name: 'Crazy Water Pavilion', distance: '3 min drive', description: 'Perfect stop after exploring local history' }
    ],
    directions: 'Find us at 103 NE 1st Avenue, right in the heart of downtown Mineral Wells. We\'re in the beautifully restored historic building across from the Baker Hotel.',
    whyLoveUs: 'As your neighbors, we take pride in being Mineral Wells\' go-to dessert destination. Our historic downtown location makes us the perfect stop for locals and tourists alike exploring our beautiful city.',
    testimonials: [
      { text: 'Best ice cream in town! My kids beg to come here every weekend.', author: 'Sarah M., Mineral Wells resident' },
      { text: 'Love supporting local business. The Stackers are incredible!', author: 'Mike T., Downtown regular' },
      { text: 'We bring all our out-of-town guests here. Always a hit!', author: 'The Johnson Family' }
    ],
    specialEvents: 'Host your birthday parties, team celebrations, and special events right here in downtown Mineral Wells!',
    nearbyAttractions: ['Baker Hotel Tours', 'Mineral Wells State Park', 'Clark Gardens', 'Fossil Park']
  },
  'ice-cream-weatherford-tx': {
    city: 'Weatherford',
    distance: '18 miles from',
    driveTime: '25 min',
    tagline: 'Worth Every Mile from Weatherford',
    heroTitle: 'Weatherford\'s Favorite Ice Cream Day Trip',
    heroSubtitle: 'Just a quick 25-minute scenic drive from Weatherford! Treat your family to award-winning ice cream in historic Mineral Wells.',
    metaDescription: 'Ice cream near Weatherford, TX - Just 25 min away! Visit 1st Ave ICP in Mineral Wells for all-natural ice cream, famous Stackers & pastries. Worth the drive!',
    keywords: ['ice cream near Weatherford TX', 'dessert Weatherford', 'ice cream shop Parker County', 'day trip from Weatherford'],
    localLandmarks: [
      { name: 'Weatherford Square', distance: '25 min drive', description: 'Head west on US-180 for a scenic drive' },
      { name: 'Chandor Gardens', distance: '20 min drive', description: 'Combine your visit with this beautiful attraction' },
      { name: 'First Monday Trade Days', distance: '25 min', description: 'Perfect ice cream stop after the market!' }
    ],
    directions: 'From Weatherford, take US-180 West for about 18 miles. You\'ll enjoy beautiful Texas countryside views! We\'re located at 103 NE 1st Ave in downtown Mineral Wells.',
    whyLoveUs: 'Weatherford families have been making the trip to our shop for years! The scenic drive along US-180 is half the fun, and our handcrafted ice cream is the perfect reward.',
    testimonials: [
      { text: 'We drive from Weatherford every other week. The kids love it!', author: 'The Martinez Family' },
      { text: 'Better than anything in Parker County. Trust me!', author: 'Jennifer K., Weatherford' },
      { text: 'My weekend treat - coffee and a Stacker. Perfection!', author: 'David R., Weatherford business owner' }
    ],
    specialEvents: 'Planning a Weatherford group outing? We can accommodate birthday parties and special events!',
    nearbyAttractions: ['Drive past Lake Weatherford', 'Historic Downtown Weatherford', 'Chandor Gardens', 'Clark Gardens']
  },
  'ice-cream-azle-tx': {
    city: 'Azle',
    distance: '25 miles from',
    driveTime: '35 min',
    tagline: 'Azle\'s Sweet Escape',
    heroTitle: 'Azle Families Love Our Ice Cream',
    heroSubtitle: 'Make it a fun family adventure! Drive 35 minutes from Azle to experience the best handcrafted ice cream in North Texas.',
    metaDescription: 'Ice cream near Azle, TX - 35 min drive to the best ice cream in the region! 1st Ave ICP in Mineral Wells serves all-natural ice cream & famous Stackers.',
    keywords: ['ice cream near Azle TX', 'dessert near Azle', 'family outing Azle', 'ice cream Tarrant County'],
    localLandmarks: [
      { name: 'Eagle Mountain Lake', distance: 'On the way!', description: 'Stop for ice cream after a day at the lake' },
      { name: 'Azle Downtown', distance: '35 min drive', description: 'Take FM 730 to US-180 West' },
      { name: 'Northwest ISD Area', distance: '40 min', description: 'Perfect after-game treat destination' }
    ],
    directions: 'From Azle, head north on FM 730, then west on US-180. The drive takes you through beautiful countryside. Look for us at 103 NE 1st Ave in downtown Mineral Wells!',
    whyLoveUs: 'Azle residents know: when you want REAL ice cream made with quality ingredients, the drive to Mineral Wells is absolutely worth it. No chains, no shortcuts - just pure deliciousness.',
    testimonials: [
      { text: 'After Eagle Mountain Lake, this is our tradition!', author: 'The Thompson Family, Azle' },
      { text: 'My book club drives here monthly. Best girls\' day out!', author: 'Patricia S., Azle' },
      { text: 'Brought my Little League team here. They loved it!', author: 'Coach Williams, Azle Youth Sports' }
    ],
    specialEvents: 'Team celebrations, birthday parties, and group outings welcome! Perfect for Azle sports teams.',
    nearbyAttractions: ['Eagle Mountain Lake', 'Lake Worth', 'Northwest Nature Center', 'Mineral Wells State Park']
  },
  'ice-cream-aledo-tx': {
    city: 'Aledo',
    distance: '28 miles from',
    driveTime: '35 min',
    tagline: 'Aledo\'s Hidden Gem Discovery',
    heroTitle: 'Aledo Residents\' Favorite Sweet Spot',
    heroSubtitle: 'Discover why Aledo families make the 35-minute drive for our famous ice cream Stackers and all-natural treats!',
    metaDescription: 'Ice cream near Aledo, TX - Worth the 35 min drive! 1st Ave ICP serves handcrafted, all-natural ice cream & pastries. Aledo families love us!',
    keywords: ['ice cream near Aledo TX', 'dessert near Aledo', 'family outing from Aledo', 'best ice cream near Parker County'],
    localLandmarks: [
      { name: 'Aledo Town Center', distance: '35 min drive', description: 'Take I-20 West to US-180' },
      { name: 'Aledo High School', distance: '35 min', description: 'Celebrate those Bearcat wins with ice cream!' },
      { name: 'Walsh Ranch', distance: '30 min', description: 'Perfect family day trip destination' }
    ],
    directions: 'From Aledo, take I-20 West to US-180 West toward Mineral Wells. It\'s a beautiful drive through the Texas countryside! Find us at 103 NE 1st Ave.',
    whyLoveUs: 'Aledo families appreciate quality, and that\'s exactly what we deliver. Our commitment to all-natural ingredients and handcrafted recipes resonates with the Aledo community.',
    testimonials: [
      { text: 'Go Bearcats! We celebrate every win here!', author: 'Aledo Football Family' },
      { text: 'The quality is unmatched. Worth every mile from Aledo.', author: 'The Richardson Family' },
      { text: 'Our Sunday tradition - church then ice cream!', author: 'The Hendersons, Aledo' }
    ],
    specialEvents: 'Aledo team celebrations and school group outings welcome! Book your party today.',
    nearbyAttractions: ['Aledo Town Center', 'Willow Park', 'Weatherford', 'Mineral Wells State Park']
  },
  'ice-cream-willow-park-tx': {
    city: 'Willow Park',
    distance: '22 miles from',
    driveTime: '30 min',
    tagline: 'Willow Park\'s Ice Cream Destination',
    heroTitle: 'Willow Park\'s Go-To Ice Cream Shop',
    heroSubtitle: 'Just 30 minutes from Willow Park! Escape the ordinary and treat your family to extraordinary handcrafted ice cream.',
    metaDescription: 'Ice cream near Willow Park, TX - Only 30 min away! 1st Ave ICP in Mineral Wells offers all-natural ice cream, Stackers & pastries. Family favorite!',
    keywords: ['ice cream near Willow Park TX', 'dessert Willow Park', 'ice cream Parker County', 'family outing Willow Park'],
    localLandmarks: [
      { name: 'Willow Park City Center', distance: '30 min drive', description: 'Head west on I-20 to US-180' },
      { name: 'Trinity Christian Academy', distance: '30 min', description: 'Perfect for school celebrations' },
      { name: 'Crown Pointe', distance: '30 min', description: 'Great weekend family trip destination' }
    ],
    directions: 'From Willow Park, take I-20 West, then exit onto US-180 West toward Mineral Wells. We\'re at 103 NE 1st Ave in the historic downtown!',
    whyLoveUs: 'Willow Park residents love our commitment to quality and our welcoming atmosphere. Many families have made us their regular weekend destination!',
    testimonials: [
      { text: 'Our family\'s favorite escape from the I-20 hustle!', author: 'The Garcia Family, Willow Park' },
      { text: 'TCA families love meeting here after games!', author: 'Trinity Christian parent' },
      { text: 'Best date night dessert spot. Unique and romantic!', author: 'Willow Park couple' }
    ],
    specialEvents: 'School groups, church outings, and birthday parties from Willow Park always welcome!',
    nearbyAttractions: ['Hudson Oaks', 'Weatherford Premium Outlets', 'Lake Mineral Wells', 'Clark Gardens']
  },
  'ice-cream-springtown-tx': {
    city: 'Springtown',
    distance: '20 miles from',
    driveTime: '25 min',
    tagline: 'Springtown\'s Sweet Destination',
    heroTitle: 'Springtown Loves Our Ice Cream',
    heroSubtitle: 'Only 25 minutes from Springtown! Your family deserves the best - all-natural, handcrafted ice cream awaits.',
    metaDescription: 'Ice cream near Springtown, TX - Just 25 min drive! 1st Ave ICP serves the best handcrafted ice cream, famous Stackers & pastries near Springtown.',
    keywords: ['ice cream near Springtown TX', 'dessert Springtown', 'family outing Springtown', 'ice cream shop near Springtown'],
    localLandmarks: [
      { name: 'Downtown Springtown', distance: '25 min drive', description: 'Take FM 51 South to US-180 West' },
      { name: 'Springtown High School', distance: '25 min', description: 'Celebrate Porcupine victories!' },
      { name: 'Springtown City Park', distance: '25 min', description: 'Great combo with park day!' }
    ],
    directions: 'From Springtown, take FM 51 South, then US-180 West to Mineral Wells. Easy drive with country views! We\'re at 103 NE 1st Ave downtown.',
    whyLoveUs: 'Springtown families have discovered that our shop is the perfect destination for quality family time. Real ingredients, real flavors, real memories!',
    testimonials: [
      { text: 'Way better than any chain! Springtown\'s secret find.', author: 'Longtime Springtown resident' },
      { text: 'Go Porcupines! We come here after every home game.', author: 'Springtown Sports Family' },
      { text: 'Best birthday party destination for our kids!', author: 'The Smiths, Springtown' }
    ],
    specialEvents: 'Springtown birthday parties, team celebrations, and group outings - we\'ve got you covered!',
    nearbyAttractions: ['Lake Bridgeport', 'Azle', 'Weatherford', 'Possum Kingdom Lake']
  },
  'ice-cream-graford-tx': {
    city: 'Graford',
    distance: '15 miles from',
    driveTime: '20 min',
    tagline: 'Graford & PK Lake\'s Ice Cream Stop',
    heroTitle: 'Possum Kingdom Lake\'s Favorite Ice Cream',
    heroSubtitle: 'Only 20 minutes from Graford and Possum Kingdom Lake! The perfect stop before or after your lake adventures.',
    metaDescription: 'Ice cream near Graford & Possum Kingdom Lake, TX - Only 20 min! 1st Ave ICP is the perfect lake day treat stop. All-natural ice cream & Stackers!',
    keywords: ['ice cream near Graford TX', 'ice cream Possum Kingdom Lake', 'dessert PK Lake', 'treats near lake house'],
    localLandmarks: [
      { name: 'Possum Kingdom Lake', distance: '20 min drive', description: 'The perfect post-lake treat!' },
      { name: 'Graford Downtown', distance: '20 min', description: 'Head east on US-180' },
      { name: 'Hell\'s Gate', distance: '25 min', description: 'Cool down after cliff jumping!' }
    ],
    directions: 'From Graford or PK Lake, take US-180 East toward Mineral Wells. Quick and easy! Find us at 103 NE 1st Ave in historic downtown.',
    whyLoveUs: 'Lake visitors and Graford locals know: after a hot day on Possum Kingdom Lake, nothing beats our handcrafted ice cream. We\'re the perfect pit stop on your way to or from the lake!',
    testimonials: [
      { text: 'Our lake house tradition - ice cream on the way home!', author: 'PK Lake cabin owners' },
      { text: 'Best treat after a day of boating!', author: 'Summer Lake Family' },
      { text: 'We stop here every time we visit the lake!', author: 'Dallas visitors to PK Lake' }
    ],
    specialEvents: 'Lake house parties, family reunions, and group outings - perfect for PK Lake visitors!',
    nearbyAttractions: ['Possum Kingdom Lake', 'Hell\'s Gate', 'The Cliffs', 'Mineral Wells State Park']
  }
};

const GeoLandingPage = () => {
  const location = useLocation();
  const citySlug = location.pathname.replace('/', '');
  const [siteSettings, setSiteSettings] = useState({});
  const [cmsContent, setCmsContent] = useState(null);
  const [loading, setLoading] = useState(true);

  // Get fallback city-specific data
  const fallbackInfo = fallbackCityData[citySlug] || fallbackCityData['ice-cream-mineral-wells-tx'];

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch CMS content for this page
        const cmsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/page-content/${citySlug}`);
        if (cmsRes.ok) {
          const data = await cmsRes.json();
          if (data && data.sections) {
            setCmsContent(data);
          }
        }
        
        // Fetch site settings
        const settingsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`);
        if (settingsRes.ok) {
          setSiteSettings(await settingsRes.json());
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [citySlug]);

  // Use CMS content if available, otherwise use fallback
  const hasCmsContent = cmsContent && cmsContent.sections && cmsContent.sections.length > 0;
  
  // Get hero section from CMS
  const heroSection = cmsContent?.sections?.find(s => s.id === 'hero' || s.type === 'hero');
  const contentSections = cmsContent?.sections?.filter(s => s.type === 'content') || [];
  const featureSection = cmsContent?.sections?.find(s => s.type === 'features');
  const ctaSection = cmsContent?.sections?.find(s => s.type === 'cta');

  // Build meta info from CMS or fallback
  const pageTitle = cmsContent?.page_title || fallbackInfo.heroTitle;
  const metaDescription = cmsContent?.meta_description || fallbackInfo.metaDescription;
  const keywords = cmsContent?.keywords || fallbackInfo.keywords;
  const cityName = cmsContent?.city || fallbackInfo.city;
  const distance = cmsContent?.distance || fallbackInfo.distance;

  const nearbyCities = Object.entries(fallbackCityData).filter(([slug]) => slug !== citySlug);

  // If we have rich CMS content, render that layout
  if (hasCmsContent) {
    return (
      <>
        <Helmet>
          <title>{pageTitle} | 1st Ave ICP</title>
          <meta name="description" content={metaDescription} />
          <meta name="keywords" content={Array.isArray(keywords) ? keywords.join(', ') : keywords} />
          <link rel="canonical" href={`https://1staveicp.com/${citySlug}`} />
          <meta property="og:title" content={pageTitle} />
          <meta property="og:description" content={metaDescription} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={`https://1staveicp.com/${citySlug}`} />
          <meta property="og:image" content="https://images.1staveicp.com/hero-section-full-20260114160233.png" />
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "IceCreamShop",
              "name": "1st Ave ICP",
              "description": metaDescription,
              "image": "https://1staveicp.com/images/logo/logo-circle.png",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "103 NE 1st Avenue",
                "addressLocality": "Mineral Wells",
                "addressRegion": "TX",
                "postalCode": "76067",
                "addressCountry": "US"
              },
              "telephone": "+1-817-829-4878",
              "url": "https://1staveicp.com",
              "areaServed": { "@type": "City", "name": cityName },
              "priceRange": "$",
              "servesCuisine": ["Ice Cream", "Desserts", "Pastries"]
            })}
          </script>
        </Helmet>

        <div className="min-h-screen">
          {/* Hero Section */}
          <section className="relative pt-28 pb-16 sm:pt-32 sm:pb-24 px-4 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-pink-500 via-pink-400 to-purple-600" />
            <div className="absolute inset-0 bg-black/10" />
            
            <div className="relative max-w-5xl mx-auto text-center text-white">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6 border border-white/30">
                <MapPin className="w-5 h-5" />
                <span className="font-semibold">{distance}</span>
              </div>
              
              <h1 
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-4 leading-tight"
                style={{ fontFamily: 'Rowdies, cursive' }}
              >
                {heroSection?.title || pageTitle}
              </h1>
              
              {heroSection?.subtitle && (
                <p className="text-lg sm:text-xl md:text-2xl opacity-90 max-w-3xl mx-auto mb-8 leading-relaxed">
                  {heroSection.subtitle}
                </p>
              )}
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/menu">
                  <Button size="lg" className="bg-white text-pink-500 hover:bg-pink-100 font-bold px-8 py-6 text-lg shadow-xl">
                    <IceCream className="w-5 h-5 mr-2" />
                    View Our Menu
                  </Button>
                </Link>
                <Link to="/find-us">
                  <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/20 font-bold px-8 py-6 text-lg">
                    <Navigation className="w-5 h-5 mr-2" />
                    Get Directions
                  </Button>
                </Link>
              </div>
            </div>
          </section>

          {/* Breadcrumb */}
          <div className="bg-white border-b shadow-sm">
            <div className="max-w-6xl mx-auto px-4 py-3">
              <nav className="flex items-center gap-2 text-sm text-gray-600">
                <Link to="/" className="hover:text-pink-500 transition-colors">Home</Link>
                <ChevronRight className="w-4 h-4" />
                <span className="text-gray-900 font-medium">Ice Cream in {cityName}, TX</span>
              </nav>
            </div>
          </div>

          {/* Main Content from CMS */}
          <div 
            className="py-12 px-4"
            style={{
              backgroundImage: 'url(/images/misc/storefront-bg.jpg)',
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundAttachment: 'fixed'
            }}
          >
            <div className="max-w-4xl mx-auto space-y-8">
              
              {/* Content Sections from CMS */}
              {contentSections.map((section, idx) => (
                <section key={section.id || idx} className="bg-white rounded-2xl shadow-xl overflow-hidden">
                  <div className="p-6 md:p-8">
                    <h2 
                      className="text-2xl md:text-3xl font-bold text-gray-800 mb-6"
                      style={{ fontFamily: 'Rowdies, cursive' }}
                    >
                      {section.title}
                    </h2>
                    
                    {section.image_url && (
                      <div className="mb-6">
                        <img
                          src={section.image_url}
                          alt={section.image_caption || section.title}
                          className="w-full h-64 md:h-80 object-cover rounded-xl"
                        />
                        {section.image_caption && (
                          <p className="text-sm text-gray-500 mt-2 italic text-center">
                            {section.image_caption}
                          </p>
                        )}
                      </div>
                    )}
                    
                    <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                      {section.content?.split('\n\n').map((paragraph, pIdx) => (
                        <p key={pIdx} className="mb-4">{paragraph}</p>
                      ))}
                    </div>
                  </div>
                </section>
              ))}

              {/* Features Section from CMS */}
              {featureSection && featureSection.items && (
                <section className="bg-white rounded-2xl shadow-xl overflow-hidden">
                  <div className="bg-gradient-to-r from-pink-500 to-purple-500 p-6 text-white">
                    <h2 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Rowdies, cursive' }}>
                      {featureSection.title}
                    </h2>
                  </div>
                  <div className="p-6 md:p-8">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {featureSection.items.map((item, idx) => (
                        <div key={idx} className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-5 border-2 border-pink-100">
                          <span className="text-3xl mb-3 block">{item.icon}</span>
                          <h3 className="font-bold text-lg mb-2 text-gray-800">{item.title}</h3>
                          <p className="text-gray-600 text-sm whitespace-pre-line">{item.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              )}

              {/* CTA Section */}
              {ctaSection && (
                <section className="bg-gradient-to-r from-pink-500 via-purple-500 to-pink-500 rounded-2xl shadow-xl p-8 text-white text-center">
                  <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ fontFamily: 'Rowdies, cursive' }}>
                    {ctaSection.title}
                  </h2>
                  <p className="text-lg opacity-90 mb-6">{ctaSection.subtitle}</p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link to="/find-us">
                      <Button size="lg" className="bg-white text-pink-500 hover:bg-pink-100 font-bold px-8">
                        <MapPin className="w-5 h-5 mr-2" />
                        Get Directions
                      </Button>
                    </Link>
                    <a href="tel:+18178294878" onClick={() => trackEvent('phone_click', { source: 'geo_landing_hero' })}>
                      <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/20 font-bold px-8">
                        <Phone className="w-5 h-5 mr-2" />
                        Call Now
                      </Button>
                    </a>
                  </div>
                </section>
              )}

              {/* Contact Card */}
              <section className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x">
                  <div className="p-6 text-center">
                    <div className="w-14 h-14 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <MapPin className="w-7 h-7 text-pink-500" />
                    </div>
                    <h3 className="font-bold text-lg mb-2">Visit Us</h3>
                    <p className="text-gray-600">103 NE 1st Avenue</p>
                    <p className="text-gray-600">Mineral Wells, TX 76067</p>
                  </div>
                  <div className="p-6 text-center">
                    <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Phone className="w-7 h-7 text-blue-500" />
                    </div>
                    <h3 className="font-bold text-lg mb-2">Call Us</h3>
                    <a href="tel:+18178294878" onClick={() => trackEvent('phone_click', { source: 'geo_landing' })} className="text-pink-500 font-semibold text-lg hover:text-pink-600">
                      (817) 829-4878
                    </a>
                  </div>
                  <div className="p-6 text-center">
                    <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Clock className="w-7 h-7 text-purple-500" />
                    </div>
                    <h3 className="font-bold text-lg mb-2">Hours</h3>
                    <p className="text-gray-600 text-sm">Mon: {siteSettings?.hours_mon || 'Closed'}</p>
                    <p className="text-gray-600 text-sm">Tue-Thu: {siteSettings?.hours_tue || '2-8 PM'}</p>
                    <p className="text-gray-600 text-sm">Wed: {siteSettings?.hours_wed || '2-9 PM'}</p>
                    <p className="text-gray-600 text-sm">Fri-Sat: {siteSettings?.hours_fri || '12-9 PM'}</p>
                    <p className="text-gray-600 text-sm">Sun: {siteSettings?.hours_sun || '12-8 PM'}</p>
                  </div>
                </div>
              </section>

              {/* Other Cities */}
              <section className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
                <h3 className="font-bold text-xl mb-6 text-center" style={{ fontFamily: 'Rowdies, cursive' }}>
                  Also Serving These Nearby Cities:
                </h3>
                <div className="flex flex-wrap justify-center gap-3">
                  {nearbyCities.map(([slug, data]) => (
                    <Link
                      key={slug}
                      to={`/${slug}`}
                      className={`px-5 py-2.5 rounded-full font-medium transition-all ${
                        slug === citySlug
                          ? 'bg-pink-500 text-white shadow-lg'
                          : 'bg-pink-100 hover:bg-pink-200 text-gray-700 hover:shadow'
                      }`}
                    >
                      {data.city}, TX
                    </Link>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Fallback layout for cities without CMS content
  const cityInfo = fallbackInfo;

  return (
    <>
      <Helmet>
        <title>{cityInfo.heroTitle} | 1st Ave ICP - Mineral Wells, TX</title>
        <meta name="description" content={cityInfo.metaDescription} />
        <meta name="keywords" content={cityInfo.keywords.join(', ')} />
        <link rel="canonical" href={`https://1staveicp.com/${citySlug}`} />
        
        <meta property="og:title" content={`${cityInfo.heroTitle} | 1st Ave ICP`} />
        <meta property="og:description" content={cityInfo.metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://1staveicp.com/${citySlug}`} />
        <meta property="og:image" content="https://images.1staveicp.com/hero-section-full-20260114160233.png" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "IceCreamShop",
            "name": "1st Ave ICP",
            "description": cityInfo.metaDescription,
            "image": "https://1staveicp.com/images/logo/logo-circle.png",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "103 NE 1st Avenue",
              "addressLocality": "Mineral Wells",
              "addressRegion": "TX",
              "postalCode": "76067",
              "addressCountry": "US"
            },
            "telephone": "+1-817-829-4878",
            "url": "https://1staveicp.com",
            "areaServed": {
              "@type": "City",
              "name": cityInfo.city
            },
            "priceRange": "$",
            "servesCuisine": ["Ice Cream", "Desserts", "Pastries"]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative pt-8 pb-16 sm:pt-12 sm:pb-24 px-4 overflow-hidden">
          <div 
            className="absolute inset-0 bg-gradient-to-br from-pink-500 via-pink-400 to-purple-600"
          />
          <div className="absolute inset-0 bg-black/10" />
          
          <div className="relative max-w-5xl mx-auto text-center text-white">
            {/* Distance Badge */}
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6 border border-white/30">
              <Car className="w-5 h-5" />
              <span className="font-semibold">{cityInfo.distance} {cityInfo.city}</span>
              {cityInfo.driveTime !== '0 min' && (
                <>
                  <span className="w-1 h-1 bg-white rounded-full" />
                  <span>{cityInfo.driveTime} drive</span>
                </>
              )}
            </div>
            
            <h1 
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-4 leading-tight"
              style={{ fontFamily: 'Rowdies, cursive' }}
            >
              {cityInfo.heroTitle}
            </h1>
            
            <p className="text-lg sm:text-xl md:text-2xl opacity-90 max-w-3xl mx-auto mb-8 leading-relaxed">
              {cityInfo.heroSubtitle}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/menu">
                <Button size="lg" className="bg-white text-pink-500 hover:bg-pink-100 font-bold px-8 py-6 text-lg shadow-xl">
                  <IceCream className="w-5 h-5 mr-2" />
                  View Our Menu
                </Button>
              </Link>
              <Link to="/find-us">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/20 font-bold px-8 py-6 text-lg">
                  <Navigation className="w-5 h-5 mr-2" />
                  Get Directions
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="bg-white border-b shadow-sm">
          <div className="max-w-6xl mx-auto px-4 py-3">
            <nav className="flex items-center gap-2 text-sm text-gray-600">
              <Link to="/" className="hover:text-pink-500 transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-gray-900 font-medium">Ice Cream Near {cityInfo.city}, TX</span>
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div 
          className="py-12 px-4"
          style={{
            backgroundImage: 'url(/images/misc/storefront-bg.jpg)',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundAttachment: 'fixed'
          }}
        >
          <div className="max-w-6xl mx-auto space-y-8">
            
            {/* Why We're Worth the Trip */}
            <section className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="bg-gradient-to-r from-pink-500 to-purple-500 p-6 text-white">
                <h2 className="text-2xl md:text-3xl font-bold flex items-center gap-3" style={{ fontFamily: 'Rowdies, cursive' }}>
                  <Heart className="w-8 h-8" />
                  Why {cityInfo.city} Residents Love Us
                </h2>
              </div>
              <div className="p-6 md:p-8">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  {cityInfo.whyLoveUs}
                </p>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-pink-50 rounded-xl p-5 text-center border-2 border-pink-100">
                    <IceCream className="w-10 h-10 text-pink-500 mx-auto mb-3" />
                    <h3 className="font-bold text-lg mb-2">All-Natural Ingredients</h3>
                    <p className="text-gray-600 text-sm">No artificial colors, flavors, or high fructose corn syrup. Ever.</p>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-5 text-center border-2 border-purple-100">
                    <Sparkles className="w-10 h-10 text-purple-500 mx-auto mb-3" />
                    <h3 className="font-bold text-lg mb-2">Famous Stackers</h3>
                    <p className="text-gray-600 text-sm">Our signature creation that {cityInfo.city} families can't stop talking about!</p>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-5 text-center border-2 border-blue-100">
                    <Users className="w-10 h-10 text-blue-500 mx-auto mb-3" />
                    <h3 className="font-bold text-lg mb-2">Family-Owned</h3>
                    <p className="text-gray-600 text-sm">We treat every customer like family - because that's who we are.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Directions Section */}
            <section className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-6 text-white">
                <h2 className="text-2xl md:text-3xl font-bold flex items-center gap-3" style={{ fontFamily: 'Rowdies, cursive' }}>
                  <Navigation className="w-8 h-8" />
                  Driving from {cityInfo.city}?
                </h2>
              </div>
              <div className="p-6 md:p-8">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="font-bold text-xl mb-4 text-gray-800">Easy Directions</h3>
                    <p className="text-gray-700 leading-relaxed mb-4">{cityInfo.directions}</p>
                    <div className="bg-gray-50 rounded-xl p-4 border">
                      <p className="font-bold text-gray-800 mb-2">📍 Our Address:</p>
                      <p className="text-gray-700">103 NE 1st Avenue</p>
                      <p className="text-gray-700">Mineral Wells, TX 76067</p>
                      <a 
                        href="https://www.google.com/maps/dir/?api=1&destination=103+NE+1st+Ave,+Mineral+Wells,+TX+76067"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackEvent('directions_click', { source: 'geo_landing' })}
                        className="inline-flex items-center gap-2 mt-3 text-pink-500 font-semibold hover:text-pink-600"
                      >
                        <Navigation className="w-4 h-4" />
                        Open in Google Maps
                      </a>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold text-xl mb-4 text-gray-800">Landmarks Along the Way</h3>
                    <div className="space-y-3">
                      {cityInfo.localLandmarks.map((landmark, idx) => (
                        <div key={idx} className="flex items-start gap-3 bg-blue-50 rounded-lg p-4 border border-blue-100">
                          <MapPin className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="font-semibold text-gray-800">{landmark.name}</p>
                            <p className="text-sm text-gray-600">{landmark.distance} • {landmark.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Testimonials */}
            <section className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="bg-gradient-to-r from-yellow-400 to-orange-500 p-6 text-white">
                <h2 className="text-2xl md:text-3xl font-bold flex items-center gap-3" style={{ fontFamily: 'Rowdies, cursive' }}>
                  <Star className="w-8 h-8" />
                  What {cityInfo.city} Customers Say
                </h2>
              </div>
              <div className="p-6 md:p-8">
                <div className="grid md:grid-cols-3 gap-6">
                  {cityInfo.testimonials.map((testimonial, idx) => (
                    <div key={idx} className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-6 border-2 border-pink-100">
                      <div className="flex gap-1 mb-3">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-5 h-5 text-yellow-500 fill-current" />
                        ))}
                      </div>
                      <p className="text-gray-700 italic mb-4">"{testimonial.text}"</p>
                      <p className="text-sm font-semibold text-pink-600">— {testimonial.author}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Special Events */}
            <section className="bg-gradient-to-r from-pink-500 via-purple-500 to-pink-500 rounded-2xl shadow-xl p-8 text-white text-center">
              <Calendar className="w-12 h-12 mx-auto mb-4" />
              <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ fontFamily: 'Rowdies, cursive' }}>
                Planning a {cityInfo.city} Group Event?
              </h2>
              <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto">
                {cityInfo.specialEvents}
              </p>
              <Link to="/book-event">
                <Button size="lg" className="bg-white text-pink-500 hover:bg-pink-100 font-bold px-8">
                  <Gift className="w-5 h-5 mr-2" />
                  Book Your Event
                </Button>
              </Link>
            </section>

            {/* Contact Card */}
            <section className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x">
                <div className="p-6 text-center">
                  <div className="w-14 h-14 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <MapPin className="w-7 h-7 text-pink-500" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">Visit Us</h3>
                  <p className="text-gray-600">103 NE 1st Avenue</p>
                  <p className="text-gray-600">Mineral Wells, TX 76067</p>
                </div>
                <div className="p-6 text-center">
                  <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Phone className="w-7 h-7 text-blue-500" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">Call Us</h3>
                  <a href="tel:+18178294878" className="text-pink-500 font-semibold text-lg hover:text-pink-600">
                    (817) 829-4878
                  </a>
                </div>
                <div className="p-6 text-center">
                  <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-7 h-7 text-purple-500" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">Hours</h3>
                  <p className="text-gray-600 text-sm">Mon: {siteSettings?.hours_mon || 'Closed'}</p>
                  <p className="text-gray-600 text-sm">Tue-Thu: {siteSettings?.hours_tue || '2-8 PM'}</p>
                  <p className="text-gray-600 text-sm">Wed: {siteSettings?.hours_wed || '2-9 PM'}</p>
                  <p className="text-gray-600 text-sm">Fri-Sat: {siteSettings?.hours_fri || '12-9 PM'}</p>
                  <p className="text-gray-600 text-sm">Sun: {siteSettings?.hours_sun || '12-8 PM'}</p>
                </div>
              </div>
            </section>

            {/* Nearby Attractions */}
            <section className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
              <h2 className="text-2xl font-bold mb-6 text-center" style={{ fontFamily: 'Rowdies, cursive' }}>
                <Camera className="w-6 h-6 inline mr-2 text-pink-500" />
                Make It a Day Trip from {cityInfo.city}!
              </h2>
              <p className="text-gray-600 text-center mb-6">Combine your ice cream visit with these nearby attractions:</p>
              <div className="flex flex-wrap justify-center gap-3">
                {cityInfo.nearbyAttractions.map((attraction, idx) => (
                  <span key={idx} className="px-4 py-2 bg-gradient-to-r from-pink-100 to-purple-100 rounded-full text-gray-700 font-medium">
                    {attraction}
                  </span>
                ))}
              </div>
            </section>

            {/* Other Cities */}
            <section className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
              <h3 className="font-bold text-xl mb-6 text-center" style={{ fontFamily: 'Rowdies, cursive' }}>
                Also Serving These Nearby Cities:
              </h3>
              <div className="flex flex-wrap justify-center gap-3">
                {Object.entries(fallbackCityData).map(([slug, data]) => (
                  <Link
                    key={slug}
                    to={`/${slug}`}
                    className={`px-5 py-2.5 rounded-full font-medium transition-all ${
                      slug === citySlug
                        ? 'bg-pink-500 text-white shadow-lg'
                        : 'bg-pink-100 hover:bg-pink-200 text-gray-700 hover:shadow'
                    }`}
                  >
                    {data.city}, TX
                  </Link>
                ))}
              </div>
            </section>

          </div>
        </div>
      </div>
    </>
  );
};

export default GeoLandingPage;
